import { FormEvent, useMemo, useState } from "react";
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  HardHat,
  House,
  LoaderCircle,
  Radio,
  Send,
  Users,
  Volume2,
} from "lucide-react";
import { CallAPIPostPromise } from "Common/commaon";
import "./push-notifications.css";

type RecipientType = "all" | "homeowners" | "contractors";

type SendResult = {
  message: string;
  requestedAudiences: number;
  queuedAudiences: number;
};

const recipientOptions: Array<{
  value: RecipientType;
  title: string;
  description: string;
  icon: typeof Users;
}> = [
  { value: "all", title: "All users", description: "Homeowners and contractors", icon: Users },
  { value: "homeowners", title: "Homeowners", description: "All homeowner devices", icon: House },
  { value: "contractors", title: "Contractors", description: "All contractor devices", icon: HardHat },
];

const getErrorMessage = (error: any) =>
  error?.message || error?.response?.data?.message || "Unable to send the notification. Please try again.";

function NotificationSender() {
  const [recipientType, setRecipientType] = useState<RecipientType>("all");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<SendResult | null>(null);
  const [error, setError] = useState("");

  const selectedAudience = useMemo(
    () => recipientOptions.find((option) => option.value === recipientType),
    [recipientType]
  );
  const canSend = title.trim().length > 0 && message.trim().length > 0 && !isSending;

  const handleSend = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSend) return;

    setIsSending(true);
    setError("");
    setResult(null);

    try {
      const response: any = await CallAPIPostPromise(
        "/notification-center/send-push-notification",
        { recipientType, title: title.trim(), message: message.trim() }
      );
      const data = response?.data || response;

      setResult({
        message: data?.message || "Notification sent successfully",
        requestedAudiences: Number(data?.requestedAudiences || 0),
        queuedAudiences: Number(data?.queuedAudiences || 0),
      });

      if (Number(data?.queuedAudiences || 0) > 0) {
        setTitle("");
        setMessage("");
      }
    } catch (sendError) {
      setError(getErrorMessage(sendError));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="page-content tailwind-prefix push-notification-page">
      <div className="push-notification-shell w-full">
        <div className="push-notification-hero relative isolate overflow-hidden text-white">
          <div className="pointer-events-none absolute -right-20 -top-28 -z-10 h-72 w-72 rounded-full bg-indigo-500/50 blur-3xl dark:bg-blue-700/30" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-32 left-1/4 -z-10 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl dark:bg-indigo-700/20" aria-hidden="true" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="push-hero-icon rounded-xl border border-white/20 bg-white/15 p-3">
                <Bell className="h-7 w-7" aria-hidden="true" />
              </div>
              <div>
                <p className="push-eyebrow mb-1">Notification center</p>
                <h1 className="m-0 text-2xl font-semibold tracking-tight text-white">Send a push notification</h1>
                <p className="mb-0 mt-2 max-w-2xl text-sm leading-6 text-blue-100 dark:text-slate-300">
                  Broadcast an update to the selected RELiBUILD audience. Delivery uses the same priority and sound settings as app notifications.
                </p>
              </div>
            </div>
            <div className="push-broadcast-badge flex items-center gap-2 self-start whitespace-nowrap sm:self-center">
              <Radio className="h-4 w-4" aria-hidden="true" />
              Global FCM broadcast
            </div>
          </div>
        </div>

        <div className="push-notification-layout">
          <form onSubmit={handleSend} className="push-notification-form">
            <fieldset className="push-recipient-fieldset">
              <legend>Choose recipients</legend>
              <div className="push-recipient-grid" role="radiogroup" aria-label="Notification recipients">
                {recipientOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = recipientType === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => {
                        setRecipientType(option.value);
                        setResult(null);
                        setError("");
                      }}
                      className={`push-recipient-option ${isSelected ? "is-selected" : ""}`}
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <span className={`inline-flex rounded-lg p-2 ${isSelected ? "bg-blue-600 text-white dark:bg-blue-500" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"}`}>
                          <Icon className="h-5 w-5" aria-hidden="true" />
                        </span>
                        <span className={`h-4 w-4 rounded-full border-2 ${isSelected ? "border-blue-600 bg-blue-600 ring-2 ring-blue-100 dark:border-blue-400 dark:bg-blue-400 dark:ring-blue-950" : "border-slate-300 dark:border-slate-600"}`} />
                      </div>
                      <span className="push-recipient-title">{option.title}</span>
                      <span className="push-recipient-description">{option.description}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <div className="push-field">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label htmlFor="push-title">Notification title</label>
                <span className="push-counter">{title.length}/100</span>
              </div>
              <input
                id="push-title"
                type="text"
                value={title}
                maxLength={100}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="What should users know?"
                className="push-control"
              />
            </div>

            <div className="push-field">
              <div className="mb-2 flex items-center justify-between gap-3">
                <label htmlFor="push-message">Message</label>
                <span className="push-counter">{message.length}/500</span>
              </div>
              <textarea
                id="push-message"
                value={message}
                maxLength={500}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Write a clear, concise message..."
                rows={4}
                className="push-control push-textarea"
              />
            </div>

            {error && (
              <div role="alert" className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="mb-0 text-sm font-semibold">Notification was not sent</p>
                  <p className="mb-0 mt-1 text-xs leading-5 text-red-700 dark:text-red-300">{error}</p>
                </div>
              </div>
            )}

            {result && (
              <div role="status" className={`mb-5 flex gap-3 rounded-xl border p-4 ${result.queuedAudiences > 0 ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-100" : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/40 dark:text-amber-100"}`}>
                {result.queuedAudiences > 0 ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" /> : <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />}
                <div>
                  <p className="mb-0 text-sm font-semibold">{result.message}</p>
                  <p className="mb-0 mt-1 text-xs leading-5 opacity-80">
                    Queued for {result.queuedAudiences} of {result.requestedAudiences} global audience channel{result.requestedAudiences === 1 ? "" : "s"}.
                  </p>
                </div>
              </div>
            )}

            <div className="push-submit-bar">
              <span>Review the audience and preview before sending.</span>
              <button type="submit" disabled={!canSend} className="push-submit-button">
                {isSending ? <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Send className="h-5 w-5" aria-hidden="true" />}
                {isSending ? "Sending notification..." : `Send to ${selectedAudience?.title.toLowerCase()}`}
              </button>
            </div>
          </form>

          <aside className="push-preview-column">
            <div className="push-preview-card">
              <div className="push-preview-heading">
                <Bell className="h-4 w-4 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                Live preview
              </div>
              <div className="push-device-preview">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-blue-600 p-2.5 text-white shadow-sm dark:bg-blue-500">
                    <Bell className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="mb-0 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">RELiBUILD</p>
                      <span className="text-[11px] text-slate-400 dark:text-slate-500">now</span>
                    </div>
                    <p className="mb-0 mt-2 break-words text-sm font-semibold text-slate-900 dark:text-slate-100">{title.trim() || "Notification title"}</p>
                    <p className="mb-0 mt-1 break-words text-xs leading-5 text-slate-600 dark:text-slate-300">{message.trim() || "Your message will appear here as you type."}</p>
                  </div>
                </div>
              </div>
              <div className="push-sound-note flex items-start gap-3">
                <span className="rounded-lg bg-emerald-50 p-2 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
                  <Volume2 className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <p className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">Sound enabled</p>
                  <p className="mb-0 text-xs leading-5 text-slate-500 dark:text-slate-400">Android and iOS receive the app's configured notification sound and high-priority delivery settings.</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

export default NotificationSender;
